import { type FC } from 'react';
import {
  Accordion,
  Brow,
  Button,
  Flex,
  Heading,
  LoadingDots,
  LogoBox,
  Section,
  Text,
} from '@highereducation/hen-ui';
import cn from '../../shared/utils/classNames';
import { RichText } from '../RichText';
import TcpaSection from '../TcpaSection';
import PiiFormSection from '../PiiFormSection/Default';
import { SubmissionStatus } from '../ResultsPage/types';
import type { PiiConfirmationProps } from './types';
import { usePiiValidation } from '../../utils/usePiiValidation';
import useMediaQuery from '../../hooks/useMediaQuery';

const PiiConfirmation: FC<PiiConfirmationProps> = ({
  isLoading,
  onProgramSubmit,
  firstName,
  lastName,
  email,
  phone,
  setFirstName,
  setLastName,
  setEmail,
  setPhone,
  formErrors,
  setFormErrors,
  setEditMode,
  editMode,
  piiLeadErrors,
  selectedResult,
  formHeading,
  leadSubmissionStatus,
  setLeadSubmissionStatus,
  productClickedCorrelationId,
  dslConfigId,
}) => {
  const isMobile = useMediaQuery('(max-width: 767px)');

  const { showPhoneError, showEmailError, handleSubmitClick } =
    usePiiValidation({
      phone,
      email,
      onProgramSubmit,
      setLeadSubmissionStatus,
    });

  const isSubmitting = leadSubmissionStatus === SubmissionStatus.SUBMITTING;
  const submitText =
    isLoading || isSubmitting ? (
      <LoadingDots color='white' label='Submitting...' />
    ) : (
      'Confirm & Submit'
    );

  const accordionItems = selectedResult?.valueProps?.map(
    (highlight: { title: string; description: string }, index: number) => ({
      title: highlight.title,
      value: `value-prop-${index}`,
      content: (
        <RichText variant='b5' color='default' html={highlight.description} />
      ),
    }),
  );

  const tcpa = selectedResult?.tcpa;
  const tcpaSecondParagraph = selectedResult?.tcpaSecondParagraph;
  const schoolLogoUrl =
    selectedResult?.imageURL ||
    (selectedResult?.schoolSlug
      ? `https://simple-storage-server.highereducation.com/${selectedResult.schoolSlug}.png?w=130&h=80`
      : undefined);

  return (
    <Flex
      asChild
      direction='column'
      gap='none'
      width='full'
      className='not-prose relative h-full bg-white md:h-auto md:min-h-full'
    >
      <form onSubmit={(e) => e.preventDefault()} data-tf-element-role='offer'>
        <Flex
          asChild
          direction='row'
          gap='lg'
          align='start'
          className={cn(
            'shrink-0 bg-white px-lg py-md',
            'border-b border-neutral-200 md:border-b-0',
          )}
        >
          <header>
            {schoolLogoUrl && (
              <LogoBox
                src={schoolLogoUrl}
                alt={selectedResult?.schoolName ?? ''}
                className='p-xxs shadow-xl'
              />
            )}
            <Flex direction='column' gap='min' className='min-w-0 flex-1'>
              <Heading variant='h4' className='truncate text-h6 md:text-h4'>
                {selectedResult?.schoolName}
              </Heading>
              <Text variant='b4' color='default'>
                {[selectedResult?.city, selectedResult?.state]
                  .filter(Boolean)
                  .join(', ')}
              </Text>
            </Flex>
          </header>
        </Flex>

        {isMobile ? (
          <Flex
            direction='column'
            gap='lg'
            className='min-h-0 w-full min-w-0 flex-1 overflow-y-auto bg-neutral-50 px-lg py-lg'
            data-vaul-no-drag
          >
            <Heading variant='h3' className='text-h4'>
              {selectedResult?.programName}
            </Heading>

            <Flex direction='column' gap='sm'>
              <PiiFormSection
                onSubmit={onProgramSubmit}
                header={formHeading ?? 'Confirm your contact details:'}
                setEditMode={setEditMode}
                editMode={editMode}
                piiLeadErrors={piiLeadErrors}
                location='single-program'
                firstName={firstName}
                lastName={lastName}
                email={email}
                phone={phone}
                setFirstName={setFirstName}
                setLastName={setLastName}
                setEmail={setEmail}
                setPhone={setPhone}
                setFormErrors={setFormErrors}
                formErrors={formErrors}
                productClickedCorrelationId={productClickedCorrelationId}
                showPhoneError={showPhoneError}
                showEmailError={showEmailError}
                dslConfigId={dslConfigId}
              />
            </Flex>

            <TcpaSection
              tcpa={tcpa}
              schoolId={selectedResult?.schoolSlug}
              schoolName={selectedResult?.schoolName}
              programFlow={selectedResult?.programFlow}
              tcpaSecondParagraph={tcpaSecondParagraph}
              providerName={selectedResult?.providerName}
            />

            <Button
              variant='primaryFill'
              size='lg'
              className='w-full'
              disabled={isLoading || isSubmitting}
              id='reward_click_lds'
              data-testid='edumatch-leaddelivery-submit'
              data-tf-element-role='submit'
              onClick={handleSubmitClick}
              type='button'
            >
              {submitText}
            </Button>

            <Flex direction='column' gap='xxs'>
              <Brow className='normal-case'>School Highlights</Brow>
              {selectedResult?.snippet && (
                <RichText
                  variant='b4'
                  color='subtext'
                  html={selectedResult.snippet}
                />
              )}
            </Flex>

            {accordionItems && accordionItems.length > 0 && (
              <Accordion
                type='single'
                collapsible
                variant='no-background'
                defaultValue='value-prop-0'
                items={accordionItems}
              />
            )}
          </Flex>
        ) : (
          <Flex
            direction='row'
            gap='2xl'
            height='full'
            className='w-full min-w-0 bg-neutral-50 px-2xl py-2xl'
          >
            <Section
              as='section'
              gap='md'
              padding='none'
              aria-label='School highlights'
              className='min-w-0 max-w-[860px] flex-1'
            >
              <Flex direction='column' gap='xxs'>
                <Brow className='normal-case'>School Highlights</Brow>
                <Heading variant='h3'>{selectedResult?.programName}</Heading>
                {selectedResult?.snippet && (
                  <RichText
                    variant='b4'
                    color='subtext'
                    className='mt-xxs'
                    html={selectedResult.snippet}
                  />
                )}
              </Flex>
              {accordionItems && accordionItems.length > 0 && (
                <Accordion
                  type='single'
                  collapsible
                  variant='no-background'
                  defaultValue='value-prop-0'
                  items={accordionItems}
                />
              )}
            </Section>

            <Section
              as='section'
              gap='md'
              padding='none'
              aria-label='Contact form'
              className={cn('min-w-0 max-w-[860px] flex-1')}
            >
              <Flex direction='column' gap='sm'>
                <PiiFormSection
                  onSubmit={onProgramSubmit}
                  header={formHeading ?? 'Confirm your contact details:'}
                  setEditMode={setEditMode}
                  editMode={editMode}
                  piiLeadErrors={piiLeadErrors}
                  location='single-program'
                  firstName={firstName}
                  lastName={lastName}
                  email={email}
                  phone={phone}
                  setFirstName={setFirstName}
                  setLastName={setLastName}
                  setEmail={setEmail}
                  setPhone={setPhone}
                  setFormErrors={setFormErrors}
                  formErrors={formErrors}
                  productClickedCorrelationId={productClickedCorrelationId}
                  showPhoneError={showPhoneError}
                  showEmailError={showEmailError}
                  dslConfigId={dslConfigId}
                />
              </Flex>

              <Flex direction='row' gap='xs'>
                <Text variant='b5' color='default'>
                  Start a conversation with a school admin by submitting your
                  contact information. Once you submit, you’ll be able to start
                  your application.
                </Text>
              </Flex>
              <Button
                variant='primaryFill'
                size='lg'
                className='w-full'
                disabled={isLoading || isSubmitting}
                id='reward_click_lds'
                data-testid='edumatch-leaddelivery-submit'
                data-tf-element-role='submit'
                onClick={handleSubmitClick}
                type='button'
              >
                {submitText}
              </Button>
              <Text variant='b5' weight='bold' color='default'>
                Your information will never be shared with a school without your
                consent.
              </Text>

              <TcpaSection
                tcpa={tcpa}
                schoolId={selectedResult?.schoolSlug}
                schoolName={selectedResult?.schoolName}
                programFlow={selectedResult?.programFlow}
                tcpaSecondParagraph={selectedResult?.tcpaSecondParagraph}
                providerName={selectedResult?.providerName}
              />
            </Section>
          </Flex>
        )}
      </form>
    </Flex>
  );
};

export default PiiConfirmation;
